"use client"

import { Brain, Bot, Building2, CheckCircle2 } from "lucide-react"

const expertiseAreas = [
    {
        icon: Brain,
        title: "AI Strategy",
        description:
            "Defining clear, practical AI roadmaps that align with business goals helping teams decide what to build, what to automate, and where AI creates real leverage.",
        points: [
            "Use-case prioritization",
            "AI roadmap & architecture decisions",
            "Build vs buy evaluation",
            "Responsible AI & governance",
        ],
    },
    {
        icon: Bot,
        title: "AI Agents",
        description:
            "Designing and deploying AI agents that can reason, plan, and act across systems moving from experimental agents to reliable, production-ready workflows.",
        points: [
            "Agentic workflows for real tasks",
            "Multi-agent systems in production",
            "Tool-calling & reasoning pipelines",
            "Monitoring and fail-safes",
        ],
    },
    {
        icon: Building2,
        title: "Enterprise AI",
        description:
            "Building secure, scalable AI solutions that integrate with existing infrastructure focused on automation, efficiency, and measurable business impact.",
        points: [
            "Production-ready AI systems",
            "Integration with existing platforms",
            "Scalable & secure deployments",
            "Measurable business outcomes",
        ],
    },
]

export default function CoreExpertise() {
    return (
        <section className="relative py-20 bg-[#020617] overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-0 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[150px] -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px]" />
            </div>

            <div className="max-w-[1800px] mx-auto px-8 lg:px-12 relative z-10">
                {/* Section Header with exact Image Style */}
                <div className="flex items-center justify-center gap-6 mb-24">
                    <div className="hidden md:block h-px w-24 bg-white/20" />
                    <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-[family-name:var(--font-plus-jakarta-sans)]">
                        Core Expertise
                    </h2>
                    <div className="hidden md:block h-px w-24 bg-white/20" />
                </div>

                {/* Expertise Columns Layout with Gap */}
                {/* Expertise Columns Layout: Connected (No Gap) + Gradient Rise */}
                <div className="grid grid-cols-1 md:grid-cols-3 bg-[#020617] rounded-[32px] overflow-hidden divide-y md:divide-y-0 md:divide-x divide-white/10 border border-white/5">
                    {expertiseAreas.map((area, index) => {
                        // "Gradient Rise" - Proportional Rise with Subtle Top Tint
                        // Top: Very subtle tint (#163A98/5)
                        // Middle: Dark (#020617)
                        // Bottom: Proportional Rise (20% -> 60% -> 100%)
                        const cardStyle = index === 0
                            ? "bg-gradient-to-b from-[#163A98]/5 via-[#020617] via-50% to-[#163A98]/20"
                            : index === 1
                                ? "bg-gradient-to-b from-[#163A98]/5 via-[#020617] via-50% to-[#163A98]/60"
                                : "bg-gradient-to-b from-[#163A98]/5 via-[#020617] via-40% to-[#163A98]";

                        return (
                            <div key={index} className={`relative group transition-all duration-500 hover:bg-[#163A98]/20 ${cardStyle}`}>
                                <div className="p-8 md:p-10 lg:p-14 flex flex-col h-full relative z-10">
                                    {/* Icon Square */}
                                    <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center mb-10 shadow-lg">
                                        <area.icon className="w-7 h-7 text-[#0a66c2]" />
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-3xl lg:text-4xl font-extrabold text-white mb-6 font-[family-name:var(--font-plus-jakarta-sans)] leading-tight">
                                        {area.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-white/70 text-lg leading-relaxed mb-10 font-[family-name:var(--font-plus-jakarta-sans)] min-h-[120px]">
                                        {area.description}
                                    </p>

                                    {/* Points List */}
                                    <ul className="space-y-4 mt-auto">
                                        {area.points.map((point, pointIndex) => (
                                            <li key={pointIndex} className="flex items-start gap-4 text-white/80 group/item">
                                                <div className="w-1.5 h-1.5 rounded-full bg-white/40 group-hover/item:bg-white transition-colors mt-2.5" />
                                                <span className="text-lg lg:text-xl font-medium font-[family-name:var(--font-plus-jakarta-sans)] whitespace-normal lg:whitespace-nowrap">
                                                    {point}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
